import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Video, CheckCircle, Upload, Loader2, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const SendMessageModal = ({ 
  isOpen, 
  user, 
  onClose, 
  subject, 
  onSubjectChange, 
  content, 
  onContentChange, 
  videoUrl, 
  onVideoUrlChange, 
  onSend, 
  sending,
  token
}) => {
  const videoInputRef = useRef(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload MP4, WebM, or MOV.');
      return;
    }
    
    if (file.size > 100 * 1024 * 1024) {
      toast.error('File too large. Maximum 100MB allowed.');
      return;
    }
    
    setUploadingVideo(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await fetch(`${API}/api/admin/videos/upload`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      onVideoUrlChange(data.video_url);
      toast.success('Video uploaded successfully');
    } catch (err) {
      toast.error('Failed to upload video');
    }
    setUploadingVideo(false);
  };

  if (!isOpen || !user) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-card border border-border p-6 max-w-xl w-full max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          data-testid="message-user-modal"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="font-heading text-xl text-white uppercase tracking-wider">
                Send <span className="text-primary">Message</span>
              </h3>
              <p className="text-muted-foreground text-sm mt-1">
                To: {user.name} ({user.email})
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white uppercase tracking-wide text-sm">Subject</Label>
              <Input
                value={subject}
                onChange={(e) => onSubjectChange(e.target.value)}
                placeholder="e.g., Great progress this week!"
                className="bg-black border-border text-white"
                data-testid="message-subject"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white uppercase tracking-wide text-sm">Message</Label>
              <Textarea
                value={content}
                onChange={(e) => onContentChange(e.target.value)}
                placeholder="Write your message here..."
                className="bg-black border-border text-white min-h-[150px]"
                data-testid="message-content"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-white uppercase tracking-wide text-sm">
                <Video className="inline h-4 w-4 mr-2" />
                Attach Video (Optional)
              </Label>
              
              {videoUrl ? (
                <div className="bg-black/50 border border-border p-3 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-green-400">
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm">Video attached</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onVideoUrlChange('')}
                    className="text-red-400 hover:text-red-300 h-8"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    value={videoUrl}
                    onChange={(e) => onVideoUrlChange(e.target.value)}
                    placeholder="Paste video URL or upload below"
                    className="bg-black border-border text-white flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => videoInputRef.current?.click()}
                    disabled={uploadingVideo}
                    className="border-border text-white"
                  >
                    {uploadingVideo ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                  </Button>
                  <input
                    ref={videoInputRef}
                    type="file"
                    accept="video/mp4,video/webm,video/quicktime"
                    onChange={handleVideoUpload}
                    className="hidden"
                  />
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Upload MP4, WebM, or MOV (max 100MB) or paste a YouTube/video URL
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-border">
            <Button
              variant="outline"
              onClick={onClose}
              className="border-border text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={onSend}
              disabled={sending || !subject.trim() || !content.trim()}
              className="bg-primary text-black hover:bg-primary/90"
              data-testid="send-message-btn"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Send Message
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
